/**
 * volume-pricing.js — Volume / bulk pricing tier highlighter
 *
 * Powers <volume-pricing-info> custom elements rendered by
 * `snippets/volume-pricing-info.liquid`. The element wraps a <details>
 * that lists Shopify's native `variant.quantity_price_breaks` (B2B / bulk
 * tiers) in a table. The JS listens to the closest `input[name="quantity"]`
 * and highlights the row matching the current quantity.
 *
 * No network requests, no cart API — purely a visual helper.
 *
 * Import name: @theme/volume-pricing
 */

import { Component } from '@theme/component';

class VolumePricingInfo extends Component {
  /** @type {HTMLInputElement | null} */
  #qtyInput = null;
  /** @type {HTMLTableRowElement[]} */
  #rows = [];

  onConnect() {
    this.#rows = Array.from(this.querySelectorAll('tr[data-tier-min]'));
    this.#qtyInput = this.#findQuantityInput();

    if (this.#qtyInput) {
      this.on(this.#qtyInput, 'input', () => this.#update());
      this.on(this.#qtyInput, 'change', () => this.#update());
    }

    // Also listen for variant changes: when a new variant is selected, the
    // section may re-render. If not, we still refresh on the next qty change.
    this.on(document, 'variant-picker:change', () => this.#update());

    this.#update();
  }

  /**
   * @returns {HTMLInputElement | null}
   */
  #findQuantityInput() {
    // Preferred: closest ancestor form's qty input
    const form = this.closest('form') || this.closest('product-form');
    if (form) {
      const input = form.querySelector('input[name="quantity"]');
      if (input instanceof HTMLInputElement) return input;
    }
    // Fallback: nearest quantity-selector sibling within the same product form
    const parent = this.parentElement;
    if (parent) {
      const input = parent.querySelector('input[name="quantity"]');
      if (input instanceof HTMLInputElement) return input;
    }
    // Global fallback — first qty input on the page.
    const any = document.querySelector('.product-form input[name="quantity"]');
    return any instanceof HTMLInputElement ? any : null;
  }

  #update() {
    const qty = this.#qtyInput ? Math.max(1, parseInt(this.#qtyInput.value, 10) || 1) : 1;
    let activeRow = null;
    for (const row of this.#rows) {
      row.classList.remove('volume-pricing-info__row--active');
      const min = parseInt(row.dataset.tierMin || '1', 10);
      if (qty >= min) activeRow = row;
    }
    if (activeRow) activeRow.classList.add('volume-pricing-info__row--active');
  }
}

if (!customElements.get('volume-pricing-info')) {
  customElements.define('volume-pricing-info', VolumePricingInfo);
}
